import { isCustomerRole } from "@nlc/shared-types";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import { useOptionalUser } from "@/hooks/useOptionalUser";
import { useIsImpersonated } from "@/utils/isImpersonated";

/**
 * Hook to identify users in PostHog with role-based configuration
 */
export function usePosthogIdentify() {
  const posthog = usePostHog();
  const { data: userData, isError, isLoading } = useOptionalUser();
  const isImpersonated = useIsImpersonated();
  const hasIdentified = useRef(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isError || !userData?.user) {
      return;
    }

    if (hasIdentified.current) {
      return;
    }

    const user = userData.user;
    const userRoleId = user.roleId;
    const isCustomer = isCustomerRole(userRoleId);

    if (!isCustomer) {
      posthog?.capture("pageview", {
        current_path: window.location.pathname + window.location.search,
        current_url: window.location.href,
      });
    }

    const userProperties: Record<string, string> = {
      customer: isCustomer ? "Yes" : "No",
      email: user.email,
      hostname: window.location.hostname,
      name: user.name,
      platform_id: String(user.id),
      role_id: String(user.roleId),
    };

    if (isCustomer && userData.currentProjectId) {
      userProperties.company_id = String(userData.currentProjectId);
    }

    if (!isCustomer && user.teamId !== null) {
      userProperties.team_id = String(user.teamId);
    }

    posthog?.identify(String(user.id), userProperties);

    if (isCustomer && !isImpersonated) {
      posthog.set_config({
        autocapture: true,
        disable_session_recording: false,
      });
    } else {
      posthog.set_config({
        autocapture: false,
        capture_dead_clicks: false,
        capture_pageleave: false,
        capture_pageview: false,
        disable_session_recording: true,
      });
      posthog.stopSessionRecording();
    }

    hasIdentified.current = true;
  }, [userData, posthog, isImpersonated, isLoading, isError]);
}
